import StoragePanel from './StoragePanel';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import Icon, { IconButton } from './Icon';

/* Resumes and portfolios. Private unless you share them; either way the
   matcher can read them (unless you switch that off) — which is the point: a
   founder looking for exactly your background can find you through it. */

const PURPOSES = [['resume', 'Resume'], ['portfolio', 'Portfolio'], ['other', 'Other']];

const STATUS = {
  processing: 'Reading…',
  ready: null,
  no_text: 'No readable text — probably a scanned image. It is stored, but the matcher cannot use it.',
  failed: 'Could not read this file. Try saving it again as PDF.',
};

function size(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MyFiles({ notify, onChanged }) {
  const [data, setData] = useState(null);
  const [purpose, setPurpose] = useState('resume');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = useRef(null);

  const load = useCallback(() => api.files().then(setData).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  // Text is read in the background after upload; look again until it's done.
  useEffect(() => {
    if (!data?.files.some((f) => f.status === 'processing')) return undefined;
    const t = setTimeout(load, 2000);
    return () => clearTimeout(t);
  }, [data, load]);

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      await api.uploadFile(file, { purpose });
      notify?.('Uploaded — reading it now');
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function change(f, patch) {
    try {
      await api.updateFile(f.id, patch);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(f) {
    if (!window.confirm(`Delete ${f.filename}? The file and everything read from it are removed — including from any chat you sent it in.`)) return;
    try {
      await api.deleteFile(f.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="myfiles">
      {error && <p className="pe-field-error" role="alert">{error}</p>}

      {data?.files.map((f) => (
        <div className="myfile" key={f.id}>
          <div className="myfile-main">
            <span className="myfile-name">
              <Icon name={f.content_type.startsWith('image/') ? 'image' : 'file'} size={18} /> {f.filename}
            </span>
            <span className="muted-sm">
              {size(f.size_bytes)}{f.pages ? ` · ${f.pages} page${f.pages > 1 ? 's' : ''}` : ''}
            </span>
            {STATUS[f.status] && <span className={`myfile-status ${f.status}`}>{STATUS[f.status]}</span>}
          </div>
          <div className="myfile-controls">
            <select className="in sm" value={f.purpose} onChange={(e) => change(f, { purpose: e.target.value })} aria-label="What it is">
              {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select className="in sm" value={f.visibility} disabled={f.content_type.startsWith('image/')}
              title={f.content_type.startsWith('image/') ? 'Images stay private' : undefined}
              onChange={(e) => change(f, { visibility: e.target.value })} aria-label="Who can see it">
              <option value="private">Only me</option>
              <option value="members">Members can download</option>
            </select>
            {!f.content_type.startsWith('image/') && <label className="myfile-check">
              <input type="checkbox" checked={f.use_for_matching}
                onChange={(e) => change(f, { use_for_matching: e.target.checked })} />
              Use for matching
            </label>}
            <IconButton icon="download" label="Download" size="sm"
              onClick={() => api.downloadFile(f.id, f.filename).catch((e) => setError(e.message))} />
            <IconButton icon="trash" label="Delete file" size="sm" variant="danger" onClick={() => remove(f)} />
          </div>
        </div>
      ))}

      <div className="myfile-add" id="pf-files">
        <select className="in sm" value={purpose} onChange={(e) => setPurpose(e.target.value)} aria-label="What you're uploading">
          {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className="btn ghost sm" onClick={() => input.current?.click()} disabled={busy}>
          <Icon name="upload" size={18} /> {busy ? 'Uploading and reading…' : 'Upload a file'}
        </button>
        <input ref={input} type="file" hidden accept=".pdf,.docx,.txt,.md" onChange={pick} />
      </div>
      <p className="pe-hint">
        PDF, Word, .txt or .md, up to 10 MB each. Private unless you choose to share it. The matcher
        reads it so the right people can find you — it is never quoted to anyone.
      </p>
      <StoragePanel notify={notify} refreshKey={data?.used_bytes} onChanged={() => api.files().then(setData).catch(() => {})} />
    </div>
  );
}
